import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ConfirmEventType, ConfirmationService, MessageService } from 'primeng/api';
import { ToolBarItem } from 'src/app/components/table-tool-bar/table-tool-bar.component';
import { AdminService } from 'src/app/services/admin.service';
import { MainContentService } from 'src/app/services/main-content.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-template-folders',
  templateUrl: './template-folders.component.html',
  styleUrls: ['./template-folders.component.scss'],
  standalone: false
})
export class TemplateFoldersComponent implements OnInit {
  templates: any[] = [];
  selectedTemplate: any[] = [];
  loadingTemplates: boolean = true;
  canEdit: boolean = false;
  canDelete: boolean = false;

  displayDialog: boolean = false;
  editMode: boolean = false;
  templateDialog: any = {
    bsrtType: '',
    bsrt: '',
    subtype: '',
    automatic: false
  };

  bsrtTypes: string[] = [
    'Projects',
    'Purchasedocuments',
    'Purchaseinvoices',
    'Request',
    'Salesdocuments',
    'Salesinvoices',
  ]

  bsrtOptions: string[] = [];
  subtypeOptions: string[] = [];
  bsrtNamesMap: Map<string, Map<string, string>> = new Map();
  templateMap: Map<string, any> = new Map();
  showEditPermissionDialog: boolean = false;

  toolBarButtons: ToolBarItem[] = [];
  toolBarButtonsMenu: ToolBarItem[] = [];

  constructor(
    private router: Router,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private adminService: AdminService,
    private mainContentService: MainContentService,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.selectedTemplate = [];
    this.canDelete = false;
    this.canEdit = false;


    this.loadTemplates();
    this.updateToolBar();
  }

  loadTemplates(): void {
    this.loadingTemplates = true;
    this.templates = [];
    this.selectedTemplate = [];
    this.canDelete = false;
    this.canEdit = false;
    this.templateMap.clear();

    this.mainContentService.getFolderTemplates().subscribe({
      next: (response: any) => {
        this.templates = response.templates || [];
        if (this.templates) {
          this.templates.sort((a, b) => {
            if (!a.deactivated === !b.deactivated) {
              return 0;
            }
            return !a.deactivated ? -1 : 1;
          });

          this.templates.forEach(template => {
            if (template.id) {
              this.templateMap.set(template.id, template);
            }
          });
        }
        this.loadingTemplates = false;

        this.loadBsrtMappings();
        this.updateToolBar();
      },
      error: (error: any) => {
        let message = this.translateService.instant('Failed to load templates');
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: message
        });
        this.templates = [];
        this.loadingTemplates = false;

        this.updateToolBar();
      }
    });
  }

  updateToolBar(): void {
    this.toolBarButtons = [
      {
        label: this.translateService.instant('Refresh'),
        icon: 'refresh',
        align: 'left',
        action: () => {
          this.loadTemplates();
        },
      },
      {
        label: this.translateService.instant('Add'),
        icon: 'add',
        align: 'right',
        action: () => {
          this.openNewTemplateDialog();
        },
      },
      {
        label: this.translateService.instant('Edit'),
        icon: 'create',
        align: 'right',
        disabled: !this.canEdit,
        action: () => {
          this.openEditTemplateDialog();
        },
      },
      {
        label: this.translateService.instant('Enable/Disable'),
        icon: 'delete',
        align: 'right',
        disabled: !this.canDelete,
        action: () => {
          this.deleteTemplate();
        },
      }
    ];

    this.toolBarButtonsMenu = [
      {
        label: this.translateService.instant('Refresh'),
        icon: 'refresh',
        align: 'left',
        action: () => {
          this.loadTemplates();
        },
      }
    ];
  }

  onRowSelect(event: any) {
    if (this.selectedTemplate.length == 1) {
      this.canEdit = true;
    } else {
      this.canEdit = false;
    }
    this.canDelete = true;
    this.updateToolBar();
  }

  onRowUnselect(event: any) {
    const index = this.selectedTemplate.indexOf(event.data);
    if (index !== -1) {
      this.selectedTemplate.splice(index, 1);
    }
    if (this.selectedTemplate.length == 1) {
      this.canEdit = true;
    } else {
      this.canEdit = false;
    }

    if (this.selectedTemplate.length == 0) {
      this.canDelete = false;
    }

    this.updateToolBar();
  }

  deleteTemplate() {
    if (!this.selectedTemplate || this.selectedTemplate.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: this.translateService.instant('No templates selected')
      });
      return;
    }

    const hasActive = this.selectedTemplate.some(t => !t.deactivated);
    const action = hasActive ? 'disable' : 'enable';
    const actionTranslated = this.translateService.instant(action);
    const confirmMsg = this.translateService.instant('CONFIRM_ACTION', { action: actionTranslated }); this.confirmationService.confirm({
      message: confirmMsg,
      header: this.translateService.instant('Confirmation'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('Yes'),
      rejectLabel: this.translateService.instant('No'),
      accept: () => {
        this.loadingTemplates = true;

        if (hasActive) {
          const idsToDisable = this.selectedTemplate.filter(t => !t.deactivated).map(t => t.id);
          this.mainContentService.deleteFolderTemplate(idsToDisable).subscribe({
            next: (response: any) => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: this.translateService.instant('Template(s) disabled successfully')
              });
              this.loadTemplates();
            },
            error: () => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: this.translateService.instant('Failed to disable template(s)')
              });
              this.loadingTemplates = false;
            }
          });
        } else {
          const enableRequests = this.selectedTemplate
            .filter(t => t.deactivated)
            .map(template => {
              const updated = { ...template, deactivated: false };
              return this.mainContentService.saveOrUpdateTemplate(updated).toPromise();
            });

          Promise.all(enableRequests)
            .then(() => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: this.translateService.instant('Template(s) enabled successfully')
              });
              this.loadTemplates();
            })
            .catch(() => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: this.translateService.instant('Failed to enable template(s)')
              });
              this.loadingTemplates = false;
            });
        }
      },
      reject: (type: any) => {
        if (type === ConfirmEventType.CANCEL) {
          this.messageService.add({ severity: 'warn', summary: 'Cancelled', detail: this.translateService.instant('You have cancelled') });
        }
      }
    });
  }

  trackByFn(index: number, item: any): string {
    return `${item.bsrtType}-${item.bsrt}-${item.subtype}`;
  }

  openNewTemplateDialog() {
    this.templateDialog = {
      bsrtType: '',
      bsrt: '',
      subtype: '',
      automatic: false
    };
    this.editMode = false;
    this.displayDialog = true;
  }

  hideDialog() {
    this.displayDialog = false;
    this.removeWritePermission();
  }

  saveTemplate() {
    if (!this.templateDialog.bsrtType || !this.templateDialog.bsrt) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: this.translateService.instant('Please fill all required fields')
      });
      return;
    }

    this.loadingTemplates = true;

    const templateData = {
      ...this.templateDialog,
      id: this.editMode ? this.templateDialog.id : null,
      subtype: this.templateDialog.subtype === '' || this.templateDialog.subtype === undefined ? null : this.templateDialog.subtype,
    }

    if (!this.editMode) {
      delete templateData.id;
    }

    this.mainContentService.saveOrUpdateTemplate(templateData).subscribe({
      next: (response: any) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: this.translateService.instant('Template saved successfully')
        });
        this.displayDialog = false;
        this.loadTemplates();
        this.removeWritePermission();

      },
      error: (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.translateService.instant('Failed to save template')
        });
        this.loadingTemplates = false;
        this.removeWritePermission();

      }
    });
  }

  openEditTemplateDialog() {
    if (this.selectedTemplate.length === 1) {
      const selectedTemplateData = { ...this.selectedTemplate[0] };
      this.templateDialog = { ...selectedTemplateData };
      this.editMode = true;

      const originalTemplate = this.templateMap.get(selectedTemplateData.id);

      this.mainContentService.getFolderURL(originalTemplate.template).subscribe({
        next: (response: any) => {
          if (response && response.url) {
            this.templateDialog.folderURL = response.url;
          }
        },
        error: (error: any) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: this.translateService.instant('Failed to load folder URL')
          });
          this.templateDialog.folderURL = '';
        }
      });

      this.bsrtOptions = [];

      if (this.templateDialog.bsrtType) {
        this.mainContentService.bsrtCategories(this.templateDialog.bsrtType, this.translateService.currentLang).subscribe({
          next: (response: any) => {
            if (response && response.data && Array.isArray(response.data)) {
              this.bsrtOptions = response.data;

              this.displayDialog = true;
            } else {
              console.error('Invalid response format from bsrtCategories');
              this.bsrtOptions = [];
              this.displayDialog = true;
            }
          },
          error: (error: any) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: this.translateService.instant('Failed to load BSRT categories for editing')
            });
            this.bsrtOptions = [];
            this.displayDialog = true;
          }
        });
      } else {
        this.displayDialog = true;
      }
    }
  }

  onBsrtTypeChange() {
    this.templateDialog.bsrt = '';
    this.bsrtOptions = [];

    if (!this.templateDialog.bsrtType) {
      return;
    }

    this.mainContentService.bsrtCategories(this.templateDialog.bsrtType, this.translateService.currentLang).subscribe({
      next: (response: any) => {
        if (response && response.data && Array.isArray(response.data)) {
          this.bsrtOptions = response.data;
        } else {
          console.error('Invalid response format from bsrtCategories');
          this.bsrtOptions = [];
        }
      },
      error: (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.translateService.instant('Failed to load BSRT categories')
        });
        this.bsrtOptions = [];
      }
    });

  }

  isSubtypeEnabled(): boolean {
    return this.templateDialog.bsrtType === 'Projects' &&
      this.templateDialog.bsrt !== undefined &&
      this.templateDialog.bsrt !== '';
  }

  loadBsrtMappings() {
    const uniqueTypes = [...new Set(this.templates.map(t => t.bsrtType))];

    uniqueTypes.forEach(type => {
      if (this.bsrtNamesMap.has(type)) return;

      this.mainContentService.bsrtCategories(type, this.translateService.currentLang).subscribe({
        next: (response: any) => {
          if (response && response.data) {
            const typeMap = new Map<string, string>();
            response.data.forEach((item: any) => {
              typeMap.set(item.code, item.name);
            });
            this.bsrtNamesMap.set(type, typeMap);
          }
        },
        error: (error) => console.error(`Error loading BSRT mappings for ${type}:`, error)
      });
    });
  }

  getBsrtName(type: string, code: string): string {
    if (!this.bsrtNamesMap.has(type) || !this.bsrtNamesMap.get(type)?.has(code)) {
      return code;
    }
    return this.bsrtNamesMap.get(type)?.get(code) || code;
  }

  onHeaderCheckboxToggle(event: any) {
    if (event.checked) {
      this.canDelete = true;
    }
    else {
      this.canDelete = false;
    }

    this.updateToolBar();
  }

  editTemplateFolder(template: any) {
    this.mainContentService.givePermission(template.template, true).subscribe({
      next: (response: any) => {
        this.showEditPermissionDialog = true;
        window.open(template.folderURL, '_blank');
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.translateService.instant('Failed to get folder URL')
        });
      }
    });

  }

  removeWritePermission() {
    if (this.templateDialog && this.templateDialog.template) {
      this.mainContentService.givePermission(this.templateDialog.template, false).subscribe();
    }
  }

  viewTemplateFolder(template: any) {
    this.mainContentService.getFolderURL(template.template).subscribe({
      next: (response: any) => {
        if (response && response.url) {
          window.open(response.url, '_blank');
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: 'No URL',
            detail: this.translateService.instant('No folder URL found for this template')
          });
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.translateService.instant('Failed to get folder URL')
        });
      }
    });
  }

  closeEditPermissionDialog() {
    this.showEditPermissionDialog = false;
    this.removeWritePermission();
  }
}